import { z } from 'zod'

export const MIN_LEADERBOARD_DURATION_MS = 20 * 60 * 1000

export const communityRoutes = {
  discordStart: '/auth/discord/start',
  discordCallback: '/auth/discord/callback',
  deviceStart: '/auth/device/start',
  devicePoll: '/auth/device/poll',
  deviceConfirm: '/auth/device/confirm',
  deviceRevoke: '/auth/device/revoke',
  me: '/api/me',
  friends: '/api/friends',
  friendRequests: '/api/friends/requests',
  parties: '/api/parties',
  joinParty: '/api/parties/join',
  runs: '/api/runs',
  leaderboard: '/api/leaderboard',
} as const

const boundedLabel = (max: number) => z.string().trim().min(1).max(max)
const nonnegativeInt = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER)
const silverAmount = z.number().int().min(-Number.MAX_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER)

export const UserSchema = z.strictObject({
  id: z.string().regex(/^\d{17,20}$/),
  displayName: boundedLabel(80),
  avatarUrl: z.url().nullable(),
  leaderboardVisible: z.boolean(),
})
export type CommunityUser = z.infer<typeof UserSchema>

export const UserSettingsSchema = z.strictObject({ leaderboardVisible: z.boolean() })
export type UserSettings = z.infer<typeof UserSettingsSchema>

export const DeviceStartSchema = z.strictObject({
  deviceCode: z.string(),
  userCode: z.string(),
  verificationUrl: z.url(),
  expiresAt: z.iso.datetime(),
})
export const DevicePollSchema = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('pending') }),
  z.strictObject({ status: z.literal('approved'), accessToken: z.string(), user: UserSchema }),
  z.strictObject({ status: z.literal('expired') }),
])
export type DevicePoll = z.infer<typeof DevicePollSchema>

// A Discord username is unique. Only accounts that have signed in can receive a request.
export const FriendRequestInputSchema = z.strictObject({
  discordUsername: boundedLabel(32).regex(/^[a-z0-9_.]+$/i),
})
export const FriendRequestSchema = z.strictObject({
  id: z.string().uuid(),
  from: UserSchema,
  to: UserSchema,
  createdAt: z.iso.datetime(),
})
export type FriendRequest = z.infer<typeof FriendRequestSchema>
export const FriendsResponseSchema = z.strictObject({ friends: z.array(UserSchema) })
export const FriendRequestsResponseSchema = z.strictObject({
  incoming: z.array(FriendRequestSchema),
  outgoing: z.array(FriendRequestSchema),
})

export const PartySchema = z.strictObject({
  id: z.string().uuid(),
  ownerId: UserSchema.shape.id,
  inviteCode: z.string().regex(/^[A-Z0-9]{6}$/),
  members: z.array(UserSchema).max(20),
  createdAt: z.iso.datetime(),
})
export type Party = z.infer<typeof PartySchema>
export const ActivePartyResponseSchema = z.strictObject({ party: PartySchema.nullable() })

export const PartyJoinInputSchema = z.strictObject({ inviteCode: z.string().trim().regex(/^[a-z0-9]{6}$/i) })

// A snapshot contains only the current state. The server adds identity and freshness.
export const LiveSnapshotSchema = z.strictObject({
  sessionId: boundedLabel(100),
  isRunning: z.boolean(),
  characterName: boundedLabel(80).nullable(),
  className: boundedLabel(80).nullable(),
  specialization: boundedLabel(80).nullable(),
  ap: nonnegativeInt.max(9999).nullable(),
  dp: nonnegativeInt.max(9999).nullable(),
  spotName: boundedLabel(120).nullable(),
  totalSilver: silverAmount,
  silverPerHour: silverAmount,
  deathCount: nonnegativeInt.max(9999),
  elapsedMs: nonnegativeInt,
})
export type LiveSnapshot = z.infer<typeof LiveSnapshotSchema>

export const LiveMemberSchema = z.strictObject({
  user: UserSchema,
  snapshot: LiveSnapshotSchema.nullable(),
  updatedAt: z.iso.datetime().nullable(),
})
export type LiveMember = z.infer<typeof LiveMemberSchema>

export const LiveClientMessageSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('snapshot'), snapshot: LiveSnapshotSchema }),
  z.strictObject({ type: z.literal('clear') }),
])
export type LiveClientMessage = z.infer<typeof LiveClientMessageSchema>
export const LiveServerMessageSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('roster'), members: z.array(LiveMemberSchema) }),
  z.strictObject({ type: z.literal('error'), message: z.string() }),
])
export type LiveServerMessage = z.infer<typeof LiveServerMessageSchema>

export const RunItemSchema = z.strictObject({
  itemId: z.string().max(80).nullable(),
  name: boundedLabel(160),
  quantity: nonnegativeInt,
  silver: silverAmount,
})
export const RunCrystalSchema = z.strictObject({
  name: boundedLabel(160),
  silver: nonnegativeInt.nullable(),
})
export const RunDeathEventSchema = z.strictObject({
  id: boundedLabel(256),
  at: z.iso.datetime(),
  source: z.enum(['ocr', 'manual']),
  crystals: z.array(z.strictObject({
    itemId: nonnegativeInt.nullable(),
    name: boundedLabel(160),
    quantity: nonnegativeInt.min(1),
    unitPrice: nonnegativeInt.nullable(),
  })).max(100),
})
export const RunSubmissionSchema = z.strictObject({
  sessionId: boundedLabel(100),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime(),
  durationMs: nonnegativeInt,
  activeDurationMs: nonnegativeInt,
  spotName: boundedLabel(120),
  characterName: boundedLabel(80).nullable(),
  className: boundedLabel(80).nullable(),
  specialization: boundedLabel(80).nullable(),
  ap: nonnegativeInt.max(9999).nullable(),
  dp: nonnegativeInt.max(9999).nullable(),
  grossSilver: nonnegativeInt,
  netSilver: silverAmount,
  deathCount: nonnegativeInt.max(9999),
  items: z.array(RunItemSchema).max(1000),
  brokenCrystals: z.array(RunCrystalSchema).max(100),
  deathEvents: z.array(RunDeathEventSchema).max(100).optional(),
  visibility: z.enum(['public', 'friends', 'private']).optional(),
})
export type RunSubmission = z.infer<typeof RunSubmissionSchema>

export const RunRecordSchema = RunSubmissionSchema.extend({
  id: z.string().uuid(),
  user: UserSchema,
  createdAt: z.iso.datetime(),
})
export type RunRecord = z.infer<typeof RunRecordSchema>

export const LeaderboardRowSchema = z.strictObject({
  rank: z.number().int().positive(),
  runId: z.string().uuid(),
  spotName: boundedLabel(120),
  characterName: boundedLabel(80).nullable(),
  className: boundedLabel(80).nullable(),
  durationMs: nonnegativeInt,
  netSilver: silverAmount,
  netSilverPerHour: silverAmount,
  deathCount: nonnegativeInt,
  endedAt: z.iso.datetime(),
})
export type LeaderboardRow = z.infer<typeof LeaderboardRowSchema>

export const LeaderboardResponseSchema = z.strictObject({
  spotName: z.string().nullable(),
  rows: z.array(LeaderboardRowSchema),
})
